import React, { createContext, useContext, useMemo, useState } from 'react';
import { useCompositionsContext } from './CompositionsContext';

const AddCompositionContext = createContext();

const newTableStructure = {
    name: "",
    permission: "active",
    description: "",
    fields: [
        {
            name: 'Primary Key',
            type: 'uint64_t',
            default_value: '',
            description: '',
            is_index: true,
            ai: true,
            is_array: false,
            is_auto_created: false,
            is_auto_updated: false
        }
    ],
    indexes: []
}

export const AddCompositionContextProvider = ({ children }) => {
    const { databaseStructure } = useCompositionsContext();
    const [step, setStep] = useState(1);
    const [pkey, setPkey] = useState(null);

    const [structs, setStructs] = useState();
    const [customTypes, setCustomTypes] = useState([]);
    const [tables, setTables] = useState([
        newTableStructure
    ]);
    const [originalTablesState, setOriginalTablesState] = useState(null);

    const setTable = (updater, index) => {
        setTables(prev => {
            if (typeof index !== 'number' || index < 0 || index >= prev.length) {
                console.warn("Invalid index passed to setTable:", index);
                return prev;
            }
            const updated = [...prev];
            updated[index] = updater(prev[index]);
            return updated;
        });
    };

    const addNewTable = () => {
        setTables(prev => [...prev, { ...newTableStructure }]);
    };

    const removeTableById = (idx) => {
        setTables(prev => {
            if (typeof idx !== 'number' || idx < 0 || idx >= prev.length) {
                // console.warn("Invalid index passed to removeTableById:", idx);
                return prev;
            }

            return prev.filter((_, i) => i !== idx);
        });
    };

    const resetTablesToOriginalState = () => {
        if (originalTablesState) {
            setTables(originalTablesState);
        }
    };

    /**
     * Normalizes a single field from API/AI format to panel format.
     * Object fields get type = object.name and recursively normalized object; scalars get type from field.type.
     */
    const normalizeFieldFromApi = (f) => {
        const base = {
            name: f.name,
            default_value: f.default_value ?? '',
            description: f.description || '',
            is_index: false,
            ai: false,
            is_array: !!f.is_array,
            is_auto_created: !!f.is_auto_created,
            is_auto_updated: !!f.is_auto_updated
        };
        const hasObject = !!(f.object && (f.object.name || f.object.fields));
        if (hasObject || f.is_object) {
            const obj = f.object || {};
            const typeName = obj.name || f.name || 'Object';
            return {
                ...base,
                type: typeName,
                is_object: true,
                object: {
                    name: typeName,
                    description: obj.description || '',
                    fields: (obj.fields || []).map(normalizeFieldFromApi)
                }
            };
        }
        return {
            ...base,
            type: (f.type && f.type !== 'object') ? f.type : 'string',
            is_object: false
        };
    };

    /**
     * Recursively collects all inline object definitions from compositions into custom types (deduped by name).
     */
    const extractCustomTypesFromCompositions = (compositions) => {
        const byName = new Map();
        const visit = (fields) => {
            if (!Array.isArray(fields)) return;
            fields.forEach((f) => {
                const obj = f.object;
                if (obj && (obj.name || obj.fields)) {
                    const name = obj.name || f.name || 'Object';
                    if (!byName.has(name)) {
                        byName.set(name, {
                            name,
                            description: obj.description || '',
                            fields: (obj.fields || []).map(normalizeFieldFromApi)
                        });
                    }
                    visit(obj.fields);
                }
            });
        };
        compositions.forEach((comp) => {
            visit(comp.fields || []);
        });
        return Array.from(byName.values());
    };

    /**
     * Converts API composition format (name, description, fields, indexes) to panel table format,
     * parses object fields recursively, registers inline objects as custom types, and replaces tables.
     */
    const setTablesFromCompositions = (compositions) => {
        if (!Array.isArray(compositions) || compositions.length === 0) return;
        const extracted = extractCustomTypesFromCompositions(compositions);
        setCustomTypes(extracted);
        const converted = compositions.map((comp) => {
            const indexRows = (comp.indexes || []).map((ind, index) => ({
                name: ind.name || 'Primary Key',
                type: ind.type || 'uint64_t',
                default_value: ind.default_value ?? '',
                description: ind.description || '',
                is_index: true,
                ai: index === 0 && ind.ai === true,
                is_array: false,
                is_auto_created: ind.is_auto_created === true,
                is_auto_updated: ind.is_auto_updated === true
            }));
            const dataRows = (comp.fields || []).map(normalizeFieldFromApi);
            return {
                name: comp.name || '',
                permission: comp.permission || 'active',
                description: comp.description || '',
                fields: [...indexRows, ...dataRows],
                indexes: []
            };
        });
        setTables(converted);
    };

    const tablesLength = () => tables?.length || 0;

    const allCompositionsList = useMemo(() =>
        new Set(
            (databaseStructure?.compositions || [])
                .map((c) => (c?.name || ''))
                .filter(Boolean)
        ),
        [databaseStructure?.compositions]
    );

    return (
        <AddCompositionContext.Provider
            value={{
                step,
                setStep,
                pkey,
                setPkey,
                allCompositionsList,
                tables,
                setTable,
                structs,
                setStructs,
                customTypes,
                setCustomTypes,
                addNewTable,
                removeTableById,
                resetTablesToOriginalState,
                setOriginalTablesState,
                setTablesFromCompositions,
                tablesLength
            }}
        >
            {children}
        </AddCompositionContext.Provider>
    )
}

export const useAddCompositionContext = () => useContext(AddCompositionContext);